import React, { useState } from 'react';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';
import OrderService from '../order/OrderService';
import WeeklyShipService from './WeeklyShipService';
import { formatDate } from '../../utils/dateUtils';
import { toast } from 'react-toastify';

const HIGH_VALUE_THRESHOLD = 50000;

const buildRegexFormula = (boxNos) => {
  const joined = boxNos.join('|');
  return `=REGEXMATCH(TO_TEXT(I:I), "^(${joined})$")`;
};

const copyBoxNos = async (orderIds, shop) => {
  if (!orderIds.length) {
    toast.warning('No orders to copy box numbers for');
    return;
  }
  try {
    const payload = { orderIds };
    if (shop && shop !== 'all') payload.shop = shop;
    const res = await WeeklyShipService.getBoxNosFromOrderIds(payload);
    const orders = res.data || [];
    const boxNos = [];
    orders.forEach((o) => {
      (o.WeeklyShips || []).forEach((ws) => {
        if (ws.boxNo && !boxNos.includes(ws.boxNo)) boxNos.push(ws.boxNo);
      });
    });
    if (!boxNos.length) {
      toast.warning('No box numbers found for these orders');
      return;
    }
    await navigator.clipboard.writeText(buildRegexFormula(boxNos));
    toast.success(`Copied ${boxNos.length} box no.`);
  } catch (err) {
    console.error(err);
    toast.error('Failed to copy box numbers');
  }
};

const CopyBoxBtn = ({ orderIds, shop, label = 'Copy Box No.' }) => {
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    setLoading(true);
    await copyBoxNos(orderIds, shop);
    setLoading(false);
  };
  return (
    <button
      onClick={handleClick}
      disabled={loading || !orderIds.length}
      className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-green-50 border border-green-300 text-green-700 rounded-md hover:bg-green-100 disabled:opacity-40 transition-colors"
    >
      {loading ? '…' : '📋'} {label}
    </button>
  );
};

const fmt = (n) =>
  Number(n).toLocaleString('ja-JP', { maximumFractionDigits: 0 });

const amountColor = (amount) => {
  if (amount >= 200000) return 'text-red-600 font-bold';
  if (amount >= 100000) return 'text-orange-500 font-semibold';
  return 'text-yellow-600 font-semibold';
};

const ShipDateBadge = ({ shipDate }) => {
  if (!shipDate) return <span className="text-gray-400">—</span>;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${shipDate.isAir ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
      {formatDate(shipDate.shipDate)}{shipDate.isAir ? ' ✈' : ' 🚢'}
    </span>
  );
};

// ─── All-items table view ────────────────────────────────────────────────────

const ItemsTable = ({ orders }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="bg-gray-100 text-gray-600 uppercase text-xs">
          <th className="px-4 py-3 text-left">Customer</th>
          <th className="px-4 py-3 text-left">Item URL</th>
          <th className="px-4 py-3 text-left">Website</th>
          <th className="px-4 py-3 text-right">Amount (¥)</th>
          <th className="px-4 py-3 text-left">Order Status</th>
          <th className="px-4 py-3 text-left">Ship Date</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {orders.map((order) => (
          <tr key={order.id} className="hover:bg-gray-50">
            <td className="px-4 py-3 whitespace-nowrap">
              <div className="font-medium text-gray-800">
                {order.CustomerProfile?.smileName || order.smileNameInput || '—'}
              </div>
              {order.CustomerProfile?.contact && (
                <div className="text-xs text-gray-400">{order.CustomerProfile.contact}</div>
              )}
            </td>
            <td className="px-4 py-3 max-w-xs">
              {order.url ? (
                <a
                  href={order.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline truncate block max-w-xs"
                  title={order.url}
                >
                  {order.url.length > 55 ? order.url.slice(0, 55) + '…' : order.url}
                </a>
              ) : (
                <span className="text-gray-400">—</span>
              )}
            </td>
            <td className="px-4 py-3 text-gray-600">{order.website || '—'}</td>
            <td className={`px-4 py-3 text-right tabular-nums ${amountColor(order.amount)}`}>
              ¥{fmt(order.amount)}
            </td>
            <td className="px-4 py-3 text-gray-600">{order.orderStatus || '—'}</td>
            <td className="px-4 py-3">
              <ShipDateBadge shipDate={order.ShipDate} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── By-customer grouped view ────────────────────────────────────────────────

const groupByCustomer = (orders) => {
  const map = new Map();
  for (const order of orders) {
    const key = order.customerId;
    if (!map.has(key)) {
      map.set(key, {
        customerId: key,
        smileName: order.CustomerProfile?.smileName || order.smileNameInput || '—',
        contact: order.CustomerProfile?.contact || '',
        phone: order.CustomerProfile?.phone || '',
        orders: [],
        total: 0,
      });
    }
    const group = map.get(key);
    group.orders.push(order);
    group.total += Number(order.amount) || 0;
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
};

const CustomerGroup = ({ group, shop }) => {
  const [expanded, setExpanded] = useState(false);
  const orderIds = group.orders.map((o) => o.id);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
      <div className="w-full flex items-center justify-between px-5 py-3 bg-gray-50">
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex items-center gap-3 flex-1 text-left hover:opacity-80 transition-opacity"
        >
          <span className="text-lg font-semibold text-gray-800">{group.smileName}</span>
          {group.contact && <span className="text-sm text-gray-500">{group.contact}</span>}
          <span className="text-xs bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">
            {group.orders.length} item{group.orders.length !== 1 ? 's' : ''}
          </span>
        </button>
        <div className="flex items-center gap-3">
          <CopyBoxBtn orderIds={orderIds} shop={shop} />
          <span className={`text-base tabular-nums ${amountColor(group.total)}`}>
            ¥{fmt(group.total)}
          </span>
          <span
            className="text-gray-400 text-sm cursor-pointer"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? '▲' : '▼'}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-white border-b border-gray-100 text-gray-500 text-xs uppercase">
                <th className="px-4 py-2 text-left">Item URL</th>
                <th className="px-4 py-2 text-left">Website</th>
                <th className="px-4 py-2 text-right">Amount (¥)</th>
                <th className="px-4 py-2 text-left">Order Status</th>
                <th className="px-4 py-2 text-left">Ship Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {group.orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 max-w-xs">
                    {order.url ? (
                      <a
                        href={order.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline truncate block max-w-xs"
                        title={order.url}
                      >
                        {order.url.length > 55 ? order.url.slice(0, 55) + '…' : order.url}
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{order.website || '—'}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${amountColor(order.amount)}`}>
                    ¥{fmt(order.amount)}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{order.orderStatus || '—'}</td>
                  <td className="px-4 py-2">
                    <ShipDateBadge shipDate={order.ShipDate} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CustomerView = ({ orders, shop }) => {
  const groups = groupByCustomer(orders);
  return (
    <div>
      {groups.map((group) => (
        <CustomerGroup key={group.customerId} group={group} shop={shop} />
      ))}
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

const HighValueOrders = () => {
  const [shipDateId, setShipDateId] = useState('all');
  const [shop, setShop] = useState('all');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [view, setView] = useState('items'); // 'items' | 'customer'

  const handleShipDateChange = (e) => {
    setShipDateId(e.target.value);
  };

  const handleSearch = async () => {
    setLoading(true);
    setSearched(false);
    try {
      const payload = { shipDateId };
      if (shop !== 'all') payload.shop = shop;
      const res = await OrderService.getHighValueWeeklyShipOrders(payload);
      setOrders(res.data || []);
      setSearched(true);
    } catch (err) {
      console.error('Failed to fetch high-value orders:', err);
      setOrders([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = orders.reduce((s, o) => s + (Number(o.amount) || 0), 0);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">High-Value Orders</h1>
        <p className="text-sm text-gray-500 mt-1">
          Orders with amount over ¥{fmt(HIGH_VALUE_THRESHOLD)} — needs extra care
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex items-center gap-2">
          <label className="text-lg text-gray-700">Shop</label>
          <select
            value={shop}
            onChange={(e) => setShop(e.target.value)}
            className="ml-1 px-4 py-1.5 border border-gray-400 rounded-md text-sm"
          >
            <option value="all">ทั้งหมด</option>
            <option value="smile">smile</option>
            <option value="tama">tama</option>
          </select>
        </div>
        <ShipDateDropdown
          handleDataChange={handleShipDateChange}
          value={shipDateId}
        />
        <button
          onClick={handleSearch}
          disabled={loading}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Loading…' : 'Search'}
        </button>
      </div>

      {/* Results */}
      {searched && (
        <>
          {/* Summary bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                <span className="font-semibold text-gray-800">{orders.length}</span> order{orders.length !== 1 ? 's' : ''}
              </span>
              {orders.length > 0 && (
                <span className="text-sm text-gray-600">
                  Total: <span className={`font-semibold tabular-nums ${amountColor(totalAmount)}`}>¥{fmt(totalAmount)}</span>
                </span>
              )}
              {orders.length > 0 && view === 'items' && (
                <CopyBoxBtn
                  orderIds={orders.map((o) => o.id)}
                  shop={shop}
                  label="Copy All Box No."
                />
              )}
            </div>

            {/* View toggle */}
            {orders.length > 0 && (
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                <button
                  onClick={() => setView('items')}
                  className={`px-4 py-1.5 font-medium transition-colors ${view === 'items' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  All Items
                </button>
                <button
                  onClick={() => setView('customer')}
                  className={`px-4 py-1.5 font-medium transition-colors ${view === 'customer' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  By Customer
                </button>
              </div>
            )}
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              No orders over ¥{fmt(HIGH_VALUE_THRESHOLD)} found for this ship date.
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              {view === 'items' ? (
                <ItemsTable orders={orders} />
              ) : (
                <div className="p-4">
                  <CustomerView orders={orders} shop={shop} />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HighValueOrders;
